import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setFeaturedEvent } from '../redux/actions/persist/featuredEvent';

const ROTATION_INTERVAL = 30000; // 30 seconds

export const useFeaturedRotation = (events) => {
  const dispatch = useDispatch();
  const storedEvent = useSelector((state) => state.PERSIST?.featuredEvent || {});
  const storedEventRef = useRef(storedEvent);

  useEffect(() => {
    storedEventRef.current = storedEvent;
  }, [storedEvent]);

  const featuredEvents = useMemo(() => {
    return (events || []).filter((e) => e.featured);
  }, [events]);

  const [currentFeatured, setCurrentFeatured] = useState(null);

  useEffect(() => {
    if (featuredEvents.length === 0) {
      setCurrentFeatured(null);
      return;
    }

    if (featuredEvents.length === 1) {
      setCurrentFeatured(featuredEvents[0]);
      return;
    }

    let timeoutId;
    let isCancelled = false;
    
    const scheduleNextRotation = (currentEventId, delay) => {
      timeoutId = setTimeout(() => {
        if (isCancelled) return;
        
        const currentIndex = featuredEvents.findIndex((e) => e.id === currentEventId);
        const nextIndex = currentIndex !== -1 ? (currentIndex + 1) % featuredEvents.length : 0;
        const nextEvent = featuredEvents[nextIndex];
        
        const now = Date.now();
        setCurrentFeatured(nextEvent);
        dispatch(setFeaturedEvent(nextEvent.id, now + ROTATION_INTERVAL));
        
        scheduleNextRotation(nextEvent.id, ROTATION_INTERVAL);
      }, delay);
    };

    const initializeRotation = () => {
      const { eventId, nextRotationAt } = storedEventRef.current || {};
      let initialEvent = featuredEvents.find(e => e.id === eventId);
      
      const now = Date.now();
      let timeRemaining = ROTATION_INTERVAL;
      let nextEventToSet = initialEvent;

      if (!initialEvent) {
        nextEventToSet = featuredEvents[0];
      } else {
        if (nextRotationAt && nextRotationAt > now) {
          timeRemaining = nextRotationAt - now;
        } else {
          const elapsed = now - (nextRotationAt || now);
          const elapsedRotations = Math.floor(elapsed / ROTATION_INTERVAL) + 1;
          const currentIndex = featuredEvents.findIndex(e => e.id === initialEvent.id);
          const targetIndex = (currentIndex + elapsedRotations) % featuredEvents.length;
          
          nextEventToSet = featuredEvents[targetIndex];
          const remainingInCurrentSlice = ROTATION_INTERVAL - (elapsed % ROTATION_INTERVAL);
          timeRemaining = remainingInCurrentSlice;
        }
      }

      if (!isCancelled) {
        setCurrentFeatured(nextEventToSet);
        dispatch(setFeaturedEvent(nextEventToSet.id, now + timeRemaining));
        scheduleNextRotation(nextEventToSet.id, timeRemaining);
      }
    };

    initializeRotation();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [featuredEvents, dispatch]);

  const remainingEvents = useMemo(() => {
    if (!currentFeatured) return events;
    return (events || []).filter(e => !e.featured);
  }, [events, currentFeatured]);

  return { currentFeatured, remainingEvents };
};
